import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "col-md-6 offset-md-3" }
const _hoisted_2 = { class: "login-form mt-4 p-4" }
const _hoisted_3 = { class: "row g-3" }
const _hoisted_4 = { class: "col-12 status-placeholder" }
const _hoisted_5 = { class: "col-12" }
const _hoisted_6 = { class: "col-12" }
const _hoisted_7 = { class: "col-12 error-placeholder" }

import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import ErrorLabel from "@/components/ui/ErrorLabel.vue";
import { latestMessage, type WebSocketMessageEmpty, type WebSocketMessageError, type WebSocketMessageWithFields } from "@/plugins/ws";
import StatusLabel from "@/components/ui/StatusLabel.vue";
import { useToast } from "@/composables/useToast";

interface EmailProgress {
  emailProgressMessage: string | null;
  emailProgressType: string | null;
}

interface User {
  email: string;
  register: EmailProgress;
}

interface RootState {
  auth: {
    user: User;
  };
  errorLabel: {
    errorReason: string | null;
  };
}

type RegisterConfirmMessage = WebSocketMessageEmpty<'auth-register-confirm'>;
type RegisterConfirmFailMessage = WebSocketMessageError<'auth-register-confirm-fail'>;
type RegisterEmailProgressMessage = WebSocketMessageWithFields<'auth-register-email-progress', {
  emailProgressMessage: string;
  emailProgressType: string;
}>;
type RegisterCancelMessage = WebSocketMessageEmpty<'auth-register-cancel'>;
type RegisterResendMessage = WebSocketMessageEmpty<'auth-register-resend'>;

// Объединенный тип для всех сообщений
type RegisterCodeWebSocketMessageType =
    | RegisterConfirmMessage
    | RegisterConfirmFailMessage
    | RegisterEmailProgressMessage
    | RegisterCancelMessage
    | RegisterResendMessage;


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'ComponentsAuthRegisterCode',
},
  __name: 'RegisterCode',
  emits: ["switchMode"],
  setup(__props, { emit: __emit }) {



const emit = __emit;

const store = useStore<RootState>();

// Типы для WebSocket сообщений компонента RegisterCode
const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload: any) => void;

const email = ref('');
const code = ref('');
const emailProgress = ref('Prepare to sending ...');
const emailProgressStyle = ref('bg-info');
const latestMessageValue = ref(latestMessage);

const user = computed(() => store.state.auth.user);

const setErrorReason = (reason: string) => store.dispatch('errorLabel/setErrorReason', reason);
const unsetErrorReason = () => store.dispatch('errorLabel/unsetErrorReason');

const { showToast } = useToast();

watch(email, () => {
  unsetErrorReason();
});

watch(code, () => {
  unsetErrorReason();
});

// WebSocket message handler
const handleWebSocketMessage = (data: RegisterCodeWebSocketMessageType): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'auth-register-confirm':
      signalRegisterConfirm();
      break;
    case 'auth-register-confirm-fail':
      signalRegisterConfirmFail(data.reason);
      break;
    case 'auth-register-email-progress':
      signalEmailProgress(data.emailProgressMessage, data.emailProgressType);
      break;
    case 'auth-register-cancel':
      signalRegisterCancel();
      break;
    case 'auth-register-resend':
      signalRegisterResend();
      break;
  }
};

watch(latestMessageValue, handleWebSocketMessage);

const handleClickActionRegisterResend = () => {
  wsSend('authRegisterResend', {});
};

const handleClickActionRegisterConfirm = () => {
  wsSend('authRegisterCode', {
    code: code.value,
  });
};

const handleClickActionRegisterConfirmCancel = () => {
  wsSend('authRegisterCancel', {});
};

const signalRegisterConfirm = () => {
  unsetErrorReason();
  store.dispatch('auth/registerConfirm');
  store.dispatch('auth/setWelcomeScreen', true);
  showToast('Почта подтверждена', 'Регистрация завершена успешно', 5000);
  emit('switchMode', 'registerSuccess');
};

const signalRegisterConfirmFail = (payload: string) => {
  setErrorReason(payload);
  showToast('Ошибка подтверждения', payload, 15000);
};

const signalRegisterCancel = () => {
  unsetErrorReason();
  store.dispatch('auth/wsUnsetUser');
  emit('switchMode', 'register');
};

const signalEmailProgress = (message: string | null, type: string | null) => {
  unsetErrorReason();
  user.value.register.emailProgressMessage = message;
  user.value.register.emailProgressType = type;
  emailProgress.value = message === null ? 'Prepare to sending ...' : message;
  switch (type) {
    case 'error':
      emailProgressStyle.value = 'bg-danger';
      break;
    case null:
    case 'info':
    default:
      emailProgressStyle.value = 'bg-info';
      break;
  }
};

const signalRegisterResend = () => {
  emailProgress.value = 'Prepare to sending ...';
  emailProgressStyle.value = 'bg-info';
};

onMounted(() => {
  email.value = user.value.email;
  emailProgress.value = user.value.register.emailProgressMessage === null ? 'Prepare to sending ...' : user.value.register.emailProgressMessage;
  switch (user.value.register.emailProgressType) {
    case 'error':
      emailProgressStyle.value = 'bg-danger';
      break;
    case null:
    case 'info':
    default:
      emailProgressStyle.value = 'bg-info';
      break;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[6] || (_cache[6] = _createElementVNode("h4", null, "Подтвердите, что вы владелец указанной почты", -1)),
        _cache[7] || (_cache[7] = _createElementVNode("p", null, "Вам на почту отправлено письмо с кодом, укажите пожалуйста тут этот код.", -1)),
        _createElementVNode("p", null, [
          _cache[2] || (_cache[2] = _createTextVNode("Если что-то пошло не так, можно ")),
          _createElementVNode("a", {
            href: "#",
            onClick: handleClickActionRegisterResend,
            id: "btn-resend"
          }, "Отправить"),
          _cache[3] || (_cache[3] = _createTextVNode(" повторно."))
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(StatusLabel, {
            message: emailProgress.value,
            style: _normalizeStyle(emailProgressStyle.value)
          }, null, 8, ["message", "style"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _cache[4] || (_cache[4] = _createElementVNode("label", null, "Почта", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "email",
            name: "email",
            class: "form-control",
            placeholder: "Email",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
            readonly: "",
            disabled: ""
          }, null, 512), [
            [_vModelText, email.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[5] || (_cache[5] = _createElementVNode("label", null, "Код", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "text",
            name: "code",
            class: "form-control",
            placeholder: "Code",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((code).value = $event))
          }, null, 512), [
            [_vModelText, code.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(ErrorLabel)
        ]),
        _createElementVNode("div", { class: "col-12" }, [
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-dark float-end ms-3",
            onClick: handleClickActionRegisterConfirm
          }, "Подтвердить"),
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-dark float-end",
            onClick: handleClickActionRegisterConfirmCancel
          }, "Отмена")
        ])
      ])
    ])
  ]))
}
}

})